import { call, getContext, put, takeEvery } from 'redux-saga/effects';
import { consolidatedServiceActions } from './consolidatedStoreActions';
import { IPlacementSummaryRequest } from './slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { PlacementService } from 'app/services/PlacementServices/placement-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { placementSummaryConsolidatedStoreActions } from './consolidatedStoreSlice';
import { ConsolidatedPlacementTargetEndpoints } from 'app/services/PlacementServices/utils';
import { trackPromise } from 'react-promise-tracker';

const TrackInitialSectionDetailsWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-consolidated-initial-section-details-data');
const TrackBulkSectionDetailsWrapper = (fn, ...args) =>
  trackPromise(fn(...args), 'placement-consolidated-bulk-section-details-data');

export function* requestPlacemnetConsolidatedDetails(action: PayloadAction<IPlacementSummaryRequest>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');

    //reset previous data if any
    yield call(placementSummaryConsolidatedStoreActions.reset);

    const response = yield call(placementService.getConsolidatedPlacementDetails, {
      placementId: action.payload.placementId,
    });

    if (httpSuccess(response?.status)) {
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementDetails({
          placementId: response.data.placementId,
          consolidatedResult: response.data,
        }),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedDetailsSuccess(response.data));
    } else {
      yield put(
        placementSummaryConsolidatedStoreActions.setErrorStatus({
          isError: true,
          message: response?.data,
        }),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedDetailsSuccess(null));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestPlacemnetConsolidatedDetails',
        area: 'src/store/redux-store/placement-summary/consolidatedStoreSaga.ts',
      },
    });
  }
}

export function* requestPlacemnetConsolidatedInitialSectionDetails(action: PayloadAction<IPlacementSummaryRequest>) {
  try {
    const placementService: PlacementService = yield getContext('placementService');

    //reset previous data if any
    yield call(placementSummaryConsolidatedStoreActions.reset);

    const response = yield call(TrackInitialSectionDetailsWrapper, placementService.getConsolidatedPlacementDetails, {
      placementId: action.payload.placementId,
      targetEndpoints: [ConsolidatedPlacementTargetEndpoints.PlacementProfileDetailsPrformanceInitialSection],
    });

    if (httpSuccess(response?.status)) {
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementInitialDetails({
          placementId: response.data.placementId,
          consolidatedResult: response.data,
          isError: false,
          message: null,
        }),
      );
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementWfcAutomationDetails(
          response.data?.common?.placementBriefDetails?.data,
        ),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedInitialDetailsSuccess(response.data));
    } else {
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementInitialDetails({
          placementId: action.payload.placementId,
          consolidatedResult: null,
          isError: true,
          message: response?.data,
        }),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedDetailsSuccess(null));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestPlacemnetConsolidatedInitialSectionDetails',
        area: 'src/store/redux-store/placement-summary/consolidatedStoreSaga.ts',
      },
    });
  }
}

export function* requestPlacemnetConsolidatedInitialBulkSectionDetails(
  action: PayloadAction<IPlacementSummaryRequest>,
) {
  try {
    const placementService: PlacementService = yield getContext('placementService');

    const response = yield call(TrackBulkSectionDetailsWrapper, placementService.getConsolidatedPlacementDetails, {
      placementId: action.payload.placementId,
      targetEndpoints: [
        ConsolidatedPlacementTargetEndpoints.PlacementProfileDetailsPrformanceInitialBulkDetailsSection,
      ],
    });

    if (httpSuccess(response?.status)) {
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementInitialBulkDetails({
          placementId: response.data.placementId,
          consolidatedResult: response.data,
          isError: false,
          message: null,
        }),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedInitialBulkDetailsSuccess(response.data));
    } else {
      yield put(
        placementSummaryConsolidatedStoreActions.setPlacementInitialBulkDetails({
          placementId: action.payload.placementId,
          consolidatedResult: null,
          isError: true,
          message: response?.data,
        }),
      );
      yield put(consolidatedServiceActions.getPlacemnetConsolidatedDetailsSuccess(null));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'requestPlacemnetConsolidatedInitialBulkSectionDetails',
        area: 'src/store/redux-store/placement-summary/consolidatedStoreSaga.ts',
      },
    });
  }
}

export function* placementSummaryConsolidatedStoreSaga() {
  yield takeEvery(consolidatedServiceActions.getPlacemnetConsolidatedDetails.type, requestPlacemnetConsolidatedDetails);
  yield takeEvery(
    consolidatedServiceActions.getPlacemnetConsolidatedInitialDetails.type,
    requestPlacemnetConsolidatedInitialSectionDetails,
  );
  yield takeEvery(
    consolidatedServiceActions.getPlacemnetConsolidatedInitialBulkDetails.type,
    requestPlacemnetConsolidatedInitialBulkSectionDetails,
  );
}
